import { applyCommand, type Command } from "../document/commands.ts";
import { canonicalPlace, seamsOf } from "../document/layout.ts";
import { validateDocument } from "../document/layout-rules.ts";
import { seamHead, seamOf, type LayoutCommand, type SeamHead } from "../document/layout-commands.ts";
import { sameJson } from "../document/same-json.ts";
import type { MapAtmosphere, WorldDocument } from "../document/types.ts";
import type { Intent, IntentOf } from "./op-types.ts";
import { mapOf, type Resolution } from "./resolve-types.ts";

/**
 * The layout under live editing (ADR-109 decision 10, ADR-122 decision 1, ADR-086 decisions 16 and 19): how
 * each layout intent resolves against the document a replica holds, key by key, and how a layout command
 * travels as intents. How an intent reads from a frame is `layout-read.ts`'s. Every resolver checks what it writes against the rules `validateDocument` holds,
 * so the server never sequences a layout the reader would refuse: a place off the lattice, out of range or
 * over another map, a seam between maps that do not touch and a crossing past its edge are `invalid`; a move
 * that would orphan a seam, a closed state over crossings and the removal of a group a map still names are
 * `held`.
 */

/**
 * The most map groups a world takes from an operation.
 */
export const WORLD_MAP_GROUPS_MAX = 256;

/**
 * The most seams a world takes from an operation.
 */
export const WORLD_SEAMS_MAX = 1024;

/**
 * The most crossings a seam takes from an operation.
 */
export const SEAM_CROSSINGS_MAX = 64;

/**
 * Whether a document keeps every layout rule.
 *
 * @param world The document.
 * @returns True when `validateDocument` accepts it.
 */
function keepsRules(world: WorldDocument): boolean {
  try {
    validateDocument(world);
    return true;
  } catch {
    return false;
  }
}

/**
 * Whether the document accepts a command.
 *
 * @param world The document.
 * @param command The command.
 * @returns True when `applyCommand` applies it.
 */
function accepts(world: WorldDocument, command: Command): boolean {
  try {
    applyCommand(world, command);
    return true;
  } catch {
    return false;
  }
}

/**
 * Resolves a place: `invalid` when the place breaks a rule by itself (the lattice, the range, an overlap),
 * `held` when it would orphan a seam of the map.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command, nothing when the map already stands there, or the reason it is skipped.
 */
function resolvePlaceMap(world: WorldDocument, intent: IntentOf<"placeMap">): Resolution {
  const map = mapOf(world, intent.map);
  if (map === undefined) {
    return "gone";
  }
  const after = canonicalPlace(intent.place ?? undefined);
  if (sameJson(canonicalPlace(map.place), after)) {
    return [];
  }
  const command: LayoutCommand = { kind: "placeMap", mapId: map.id, ...(map.place === undefined ? {} : { before: map.place }), ...(after === undefined ? {} : { after }) };
  const { seams: _seams, ...unseamed } = world;
  const { place: _place, ...unplaced } = map;
  const moved: WorldDocument = { ...unseamed, maps: world.maps.map((m) => (m.id === map.id ? { ...unplaced, ...(after === undefined ? {} : { place: after }) } : m)) };
  if (!keepsRules(moved)) {
    return "invalid";
  }
  return accepts(world, command) ? [command] : "held";
}

/**
 * Resolves the grid.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command, nothing when the grid is already as asked, or `invalid` for a grid off the lattice.
 */
function resolveSetGrid(world: WorldDocument, intent: IntentOf<"setGrid">): Resolution {
  const after = intent.grid ?? undefined;
  if (sameJson(world.grid, after)) {
    return [];
  }
  const command: LayoutCommand = { kind: "setGrid", ...(world.grid === undefined ? {} : { before: world.grid }), ...(after === undefined ? {} : { after }) };
  return accepts(world, command) ? [command] : "invalid";
}

/**
 * Resolves a change of the map groups, group by group: a group set replaces the one of its id in place or is
 * appended, and a group removed is taken out.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command over the whole list, nothing when every group already is as asked, `held` when a group
 *   removed is still named by a map, or `limit` past `WORLD_MAP_GROUPS_MAX`.
 */
function resolveSetMapGroups(world: WorldDocument, intent: IntentOf<"setMapGroups">): Resolution {
  const before = world.mapGroups ?? [];
  const removed = new Set(intent.remove ?? []);
  const set = new Map((intent.set ?? []).map((group) => [group.id, group]));
  const kept = before.filter((group) => !removed.has(group.id)).map((group) => set.get(group.id) ?? group);
  const after = [...kept, ...[...set.values()].filter((group) => !before.some((held) => held.id === group.id))];
  if (sameJson(before, after)) {
    return [];
  }
  if (after.length > WORLD_MAP_GROUPS_MAX && after.length > before.length) {
    return "limit";
  }
  if (world.maps.some((map) => map.mapGroup !== undefined && removed.has(map.mapGroup))) {
    return "held";
  }
  const command: LayoutCommand = { kind: "setMapGroups", before, after };
  return accepts(world, command) ? [command] : "invalid";
}

/**
 * Resolves a map's group.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command, nothing when the map is already in it, or `gone` without the map or the group.
 */
function resolveSetMapGroup(world: WorldDocument, intent: IntentOf<"setMapGroup">): Resolution {
  const map = mapOf(world, intent.map);
  if (map === undefined || (intent.group !== null && !(world.mapGroups ?? []).some((group) => group.id === intent.group))) {
    return "gone";
  }
  const after = intent.group ?? undefined;
  if (map.mapGroup === after) {
    return [];
  }
  return [{ kind: "setMapGroup", mapId: map.id, ...(map.mapGroup === undefined ? {} : { before: map.mapGroup }), ...(after === undefined ? {} : { after }) }];
}

/**
 * Resolves a map's draft mark.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command, nothing when the map already holds it, or `gone` without the map.
 */
function resolveSetMapDraft(world: WorldDocument, intent: IntentOf<"setMapDraft">): Resolution {
  const map = mapOf(world, intent.map);
  if (map === undefined) {
    return "gone";
  }
  const before = map.draft === true;
  return before === intent.draft ? [] : [{ kind: "setMapDraft", mapId: map.id, before, after: intent.draft }];
}

/**
 * The command a change of one of a map's own fields resolves to, or `invalid` when the document refuses it.
 *
 * @param world The document.
 * @param command The command.
 * @returns The command, or `invalid`.
 */
function checked(world: WorldDocument, command: LayoutCommand): Resolution {
  return accepts(world, command) ? [command] : "invalid";
}

/**
 * Resolves a room's host. A host whose link is broken is no skip: the link is an issue (`hostIssues`).
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command, nothing when the map already has it, `gone` without the map, or `invalid` for a placed
 *   map or a room size the document refuses.
 */
function resolveSetMapHost(world: WorldDocument, intent: IntentOf<"setMapHost">): Resolution {
  const map = mapOf(world, intent.map);
  if (map === undefined) {
    return "gone";
  }
  const after = intent.host ?? undefined;
  if (sameJson(map.host, after)) {
    return [];
  }
  return checked(world, { kind: "setMapHost", mapId: map.id, ...(map.host === undefined ? {} : { before: map.host }), ...(after === undefined ? {} : { after }) });
}

/**
 * Resolves who shares a map.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command, nothing when the map is already so shared, `gone` without the map, or `invalid` for a
 *   party, a character or an owner on a map with no host.
 */
function resolveSetMapShare(world: WorldDocument, intent: IntentOf<"setMapShare">): Resolution {
  const map = mapOf(world, intent.map);
  if (map === undefined) {
    return "gone";
  }
  const before = map.share ?? "open";
  return before === intent.share ? [] : checked(world, { kind: "setMapShare", mapId: map.id, before, after: intent.share });
}

/**
 * Resolves a map's capacity.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command, nothing when the map already holds it, `gone` without the map, or `invalid` outside 1
 *   to 600.
 */
function resolveSetMapCapacity(world: WorldDocument, intent: IntentOf<"setMapCapacity">): Resolution {
  const map = mapOf(world, intent.map);
  if (map === undefined) {
    return "gone";
  }
  const after = intent.capacity ?? undefined;
  if (map.capacity === after) {
    return [];
  }
  return checked(world, { kind: "setMapCapacity", mapId: map.id, ...(map.capacity === undefined ? {} : { before: map.capacity }), ...(after === undefined ? {} : { after }) });
}

/**
 * The atmosphere a change leaves: each of music, ambience, light and dim the change names is set, or cleared by
 * null; the others are kept as they were. The one rule the intent's resolution and the editing tool's plan share.
 *
 * @param before The atmosphere now.
 * @param change The keys to set (a value) or clear (null); an absent key is kept.
 * @returns The atmosphere after.
 */
export function atmosphereAfter(before: MapAtmosphere, change: { readonly [K in keyof MapAtmosphere]?: MapAtmosphere[K] | null }): MapAtmosphere {
  const pick = <K extends keyof MapAtmosphere>(key: K): { [P in K]?: MapAtmosphere[K] } => {
    const value = change[key] === undefined ? before[key] : (change[key] ?? undefined);
    return (value === undefined ? {} : { [key]: value }) as { [P in K]?: MapAtmosphere[K] };
  };
  return { ...pick("music"), ...pick("ambience"), ...pick("light"), ...pick("dim") };
}

/**
 * Resolves a change of a map's atmosphere, key by key: each of its music, ambience, light and dim the intent names
 * is set, or cleared by null; the others are kept as the document holds them.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command carrying the whole atmosphere before and after, nothing when every key already holds
 *   its value, or `gone` without the map.
 */
function resolveSetMapAtmosphere(world: WorldDocument, intent: IntentOf<"setMapAtmosphere">): Resolution {
  const map = mapOf(world, intent.map);
  if (map === undefined) {
    return "gone";
  }
  const before: MapAtmosphere = map.atmosphere ?? {};
  const after = atmosphereAfter(before, intent);
  return sameJson(before, after) ? [] : checked(world, { kind: "setMapAtmosphere", mapId: map.id, before, after });
}

/**
 * Resolves a seam's head, adding the seam when the world has none of the pair.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command, nothing when the seam already holds the head, `gone` without either map, `held` for a
 *   closed state over crossings, `limit` past `WORLD_SEAMS_MAX`, or `invalid` for maps that share no edge.
 */
function resolveSetSeam(world: WorldDocument, intent: IntentOf<"setSeam">): Resolution {
  if (mapOf(world, intent.a) === undefined || mapOf(world, intent.b) === undefined) {
    return "gone";
  }
  const after: SeamHead = { state: intent.state, ...(intent.barrier === undefined ? {} : { barrier: intent.barrier }) };
  const held = seamOf(world, intent.a, intent.b);
  if (held !== undefined) {
    if (sameJson(seamHead(held), after)) {
      return [];
    }
    if (after.state === "closed" && held.crossings.length > 0) {
      return "held";
    }
    return [{ kind: "setSeam", a: held.a, b: held.b, before: seamHead(held), after }];
  }
  if ((world.seams ?? []).length >= WORLD_SEAMS_MAX) {
    return "limit";
  }
  const command: LayoutCommand = { kind: "setSeam", a: intent.a, b: intent.b, after };
  return accepts(world, command) ? [command] : "invalid";
}

/**
 * Resolves a seam's removal.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command carrying the whole seam, or nothing when the world has none of the pair.
 */
function resolveRemoveSeam(world: WorldDocument, intent: IntentOf<"removeSeam">): Resolution {
  const held = seamOf(world, intent.a, intent.b);
  return held === undefined ? [] : [{ kind: "removeSeam", seam: held }];
}

/**
 * Resolves one crossing, set by its name.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command, nothing when the crossing already is as asked, `gone` without the seam, `limit` past
 *   `SEAM_CROSSINGS_MAX`, or `invalid` for a closed seam or a span past the edge.
 */
function resolveSetCrossing(world: WorldDocument, intent: IntentOf<"setCrossing">): Resolution {
  const held = seamOf(world, intent.a, intent.b);
  if (held === undefined) {
    return "gone";
  }
  const before = held.crossings.find((crossing) => crossing.name === intent.crossing.name);
  if (sameJson(before, intent.crossing)) {
    return [];
  }
  if (before === undefined && held.crossings.length >= SEAM_CROSSINGS_MAX) {
    return "limit";
  }
  const command: LayoutCommand = { kind: "setCrossing", a: held.a, b: held.b, ...(before === undefined ? {} : { before }), after: intent.crossing };
  return accepts(world, command) ? [command] : "invalid";
}

/**
 * Resolves a crossing's removal.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command carrying the whole crossing, or nothing when the seam or the crossing is already gone.
 */
function resolveRemoveCrossing(world: WorldDocument, intent: IntentOf<"removeCrossing">): Resolution {
  const held = seamOf(world, intent.a, intent.b);
  const crossing = held?.crossings.find((c) => c.name === intent.name);
  return held === undefined || crossing === undefined ? [] : [{ kind: "removeCrossing", a: held.a, b: held.b, crossing }];
}

/**
 * The layout intent kinds.
 */
export type LayoutIntent = IntentOf<"placeMap" | "setGrid" | "setMapGroups" | "setMapGroup" | "setMapDraft" | "setMapHost" | "setMapShare" | "setMapCapacity" | "setMapAtmosphere" | "setSeam" | "removeSeam" | "setCrossing" | "removeCrossing">;

/**
 * Resolves a layout intent against a document.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns Its commands, none when it changes nothing, or the reason it is skipped.
 */
export function resolveLayoutIntent(world: WorldDocument, intent: LayoutIntent): Resolution {
  switch (intent.k) {
    case "placeMap":
      return resolvePlaceMap(world, intent);
    case "setGrid":
      return resolveSetGrid(world, intent);
    case "setMapGroups":
      return resolveSetMapGroups(world, intent);
    case "setMapGroup":
      return resolveSetMapGroup(world, intent);
    case "setMapDraft":
      return resolveSetMapDraft(world, intent);
    case "setMapHost":
      return resolveSetMapHost(world, intent);
    case "setMapShare":
      return resolveSetMapShare(world, intent);
    case "setMapCapacity":
      return resolveSetMapCapacity(world, intent);
    case "setMapAtmosphere":
      return resolveSetMapAtmosphere(world, intent);
    case "setSeam":
      return resolveSetSeam(world, intent);
    case "removeSeam":
      return resolveRemoveSeam(world, intent);
    case "setCrossing":
      return resolveSetCrossing(world, intent);
    case "removeCrossing":
      return resolveRemoveCrossing(world, intent);
  }
}

/**
 * The removals a map's removal takes with it: one for each seam that names the map as the document holds it
 * now, so a seam another builder added a moment before never outlives the map.
 *
 * @param world The document.
 * @param mapId The map.
 * @returns The seam removals, in key order.
 */
export function seamRemovalsFor(world: WorldDocument, mapId: string): Command[] {
  return seamsOf(world, mapId).map((seam) => ({ kind: "removeSeam", seam }));
}

/**
 * The intents a layout command travels as.
 *
 * @param command The command.
 * @returns The intents: one, or for a restored seam the seam and then each of its crossings.
 */
export function layoutIntents(command: LayoutCommand): Intent[] {
  switch (command.kind) {
    case "placeMap":
      return [{ k: "placeMap", map: command.mapId, place: canonicalPlace(command.after) ?? null }];
    case "setGrid":
      return [{ k: "setGrid", grid: command.after === undefined ? null : { cell: command.after.cell, x: command.after.x, y: command.after.y } }];
    case "setMapGroups": {
      const set = command.after.filter((group) => !command.before.some((held) => sameJson(held, group))).map((group) => ({ id: group.id, name: group.name, colour: group.colour }));
      const remove = command.before.filter((held) => !command.after.some((group) => group.id === held.id)).map((held) => held.id);
      return set.length === 0 && remove.length === 0 ? [] : [{ k: "setMapGroups", ...(set.length === 0 ? {} : { set }), ...(remove.length === 0 ? {} : { remove }) }];
    }
    case "setMapGroup":
      return [{ k: "setMapGroup", map: command.mapId, group: command.after ?? null }];
    case "setMapDraft":
      return command.before === command.after ? [] : [{ k: "setMapDraft", map: command.mapId, draft: command.after }];
    case "setMapHost":
      return [{ k: "setMapHost", map: command.mapId, host: command.after === undefined ? null : { map: command.after.map, arrival: command.after.arrival } }];
    case "setMapShare":
      return command.before === command.after ? [] : [{ k: "setMapShare", map: command.mapId, share: command.after }];
    case "setMapCapacity":
      return [{ k: "setMapCapacity", map: command.mapId, capacity: command.after ?? null }];
    case "setMapAtmosphere": {
      const keys = (["music", "ambience", "light", "dim"] as const).filter((key) => command.before[key] !== command.after[key]);
      return keys.length === 0 ? [] : [{ k: "setMapAtmosphere", map: command.mapId, ...Object.fromEntries(keys.map((key) => [key, command.after[key] ?? null])) }];
    }
    case "setSeam":
      return [{ k: "setSeam", a: command.a, b: command.b, state: command.after.state, ...(command.after.barrier === undefined ? {} : { barrier: command.after.barrier }) }, ...(command.crossings ?? []).map((crossing): Intent => ({ k: "setCrossing", a: command.a, b: command.b, crossing }))];
    case "removeSeam":
      return [{ k: "removeSeam", a: command.seam.a, b: command.seam.b }];
    case "setCrossing":
      return [{ k: "setCrossing", a: command.a, b: command.b, crossing: command.after }];
    case "removeCrossing":
      return [{ k: "removeCrossing", a: command.a, b: command.b, name: command.crossing.name }];
  }
}
